import { useState, useEffect } from 'react';
import moment from "moment";
import { isoCountries } from "../../config/data/iso2Country";


export const useWindowSize = () => {
    const [size, setSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return size;
};


export const actionForState = (action, state, message) => ({
  ...action,
  success: state === "success",
  loading: state === "pending",
  error: state === "error",
  message: message || state,
});


export const tConvert = (time) => {
  // Check correct time format and split into components
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time,
  ];

  if (time.length > 1) {
    // If time format correct
    time = time.slice(1); // Remove full string match value
    time[5] = +time[0] < 12 ? " AM" : " PM"; // Set AM/PM
    time[0] = +time[0] % 12 || 12; // Adjust hours
  }
  return time.join(""); // return adjusted time or original string
};


export const zeroPad = (num, places) => String(num).padStart(places, "0");


export const guestNameUpdateBookingTitle = (title, guestName, fixedSuffixParts = 2) => {
    // Ensure fallback value if guestName is empty or whitespace
    guestName = guestName.trim() || "Unknown";
    // Split all parts and normalize whitespace
    const parts = title.split("|").map(p => p.trim());
    if (parts.length >= fixedSuffixParts) {
        // Keep the last N stable segments
        const suffix = parts.slice(-fixedSuffixParts);
        return [guestName, ...suffix].join(" | ");
    }
    // If structure is unexpected, return original title
    return title;
};


export const hideTawkComponent = (hide) => {
  const tawkComponent = document.getElementsByTagName("iframe")[0];
  if (tawkComponent) {
    tawkComponent.style.display = hide ? "none" : "block";
  }
};


export const setCookie = (name, value, expiration) => {
  var expires = "";
  if (expiration) {
    var date = new Date();
    date.setTime(date.getTime() + expiration * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};


export const getCookie = (name) => {
  const value = document.cookie;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
};


export const findCountry = (val) => {
  if (val) {
    for (const key in isoCountries) {
      if (isoCountries[key] === val) {
        return key.toLocaleLowerCase();
      }
    }
  } else {
    return "us";
  }
};


const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};


export const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};


export const customSort = (array, order, orderBy, key='id') => {
  const comparator = getComparator(order, orderBy);

  // Separate parents and children
  const parents = array.filter((item) => item.parentRentalId === null);
  const children = array.filter((item) => item.parentRentalId !== null);

  // Sorts only parents
  const stabilizedParents = parents.map((el, index) => [el, index]);
  stabilizedParents.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  const orderedParents = stabilizedParents.map((el) => el[0]);

  // Sorts only children
  const stabilizedChildren = children.map((el, index) => [el, index]);
  stabilizedChildren.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  const orderedChildren = stabilizedChildren.map((el) => el[0]);

  // Attach the children to their parents
  const orderedData = [];
  orderedParents.forEach((parent) => {
    orderedData.push(parent); // Add parent
    orderedChildren.forEach((child) => {
      if (child.parentRentalId === parent[`${key}`]) {
        orderedData.push(child); // Add the child right after the parent
      }
    });
  });

  return orderedData;
};


export const customClassification = (array, order, orderBy) => {
  const comparator = getComparator(order, orderBy);

  const stabilizedThis = array.map((item, index) => [item, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
  });

  return stabilizedThis.map((item) => item[0]);
};


export const getLocalTimestamp = (inputDateStr, timeZone, format = 'YYYY-MM-DD HH:mm:ss') => {
  const date = new Date(inputDateStr);
  if (isNaN(date.getTime())) {
      throw new Error(`Invalid date string: ${inputDateStr}`);
  }

  const options = {
      timeZone: timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
  };

  let formattedDate;
  try {
      formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
  } catch (error) {
      throw new Error(`Invalid time zone: ${timeZone}`);
  }

  const normalizedDate = formattedDate
      .replace(/, /g, ' ')
      .replace(/(\d{2})\/(\d{2})\/(\d{4}) (\d{2}:\d{2}:\d{2})/, '$3-$1-$2 $4');

  return moment(normalizedDate).format(format);
};


export const addMonth = (inputDate) => {

  const dateParts = inputDate.split('-');
  let year = parseInt(dateParts[0], 10);
  let month = parseInt(dateParts[1], 10);
  let day = parseInt(dateParts[2], 10);

  // Increment the month
  month += 1;
  if (month > 12) {
      month = 1;
      year += 1;
  }

  // Handle end of month scenarios
  if (day > 28) {
      if (month === 2) {
          // Check for leap year
          if ((year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0)) {
              day = Math.min(day, 29);
          } else {
              day = Math.min(day, 28);
          }
      } else if ([4, 6, 9, 11].includes(month)) {
          day = Math.min(day, 30);
      }
  }

  // Format the new date as YYYY-MM-DD
  const newDay = day.toString().padStart(2, '0');
  const newMonth = month.toString().padStart(2, '0');
  const newDate = `${year}-${newMonth}-${newDay}`;

  return newDate;
}


export const addDaysToDateObj = (date, days) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
};


export const getDateObjFromString = (dateString) => {
  const [year, month, day] = dateString.split('-');
  const date = new Date(year, month - 1, day);
  return date;
};


export const calculateFeesAndTaxes = (feesAndTaxesList, totalDue, numberOfNights) => {
  const totalGuests = 1;
  let result = 0;

  feesAndTaxesList.forEach(item => {
      const amount = item.amount;
      const itemType = item?.feeType ? item.feeType : item.taxType;
      const modality = item.modality;

      if (itemType === "flat rate") {
          if (modality === "per person") {
              result += amount * totalGuests;
          } else if (modality === "per stay") {
              result += amount;
          } else if (modality === "per night") {
              result += amount * numberOfNights;
          } else if (modality === "pppn") {
              result += amount * totalGuests * numberOfNights;
          }
      } else if (itemType === "percentage") {
          result += totalDue * (amount / 100);
      }
  });

  return parseFloat(result.toFixed(2));
};


export const checkDateLimitForReservations = (dateLimit, unit, bookingType, date) => {

    const _date = moment(date);
    const now = moment();

    let dateLimitForReservation = now.clone().add(dateLimit, unit);

    if (bookingType === 'monthly') {
        dateLimitForReservation.subtract(1, 'months');
    } else if (bookingType === 'weekly') {
        dateLimitForReservation.subtract(1, 'weeks');
    }

    return !_date.isBefore(dateLimitForReservation);

};


export const getTheMaximumNumberOfNightsAllowed = (allowBookingFor, numberOf) => {
  const endDate = moment().add(numberOf, allowBookingFor === "months" ? "M" : "y");
  const nights = endDate.diff(moment(), "days");
  return nights > 999 ? 999 : nights;
};


export const checkDoubleBooking = (arrive, depart, allBookings, rentalId) => {

  const doubleBookingRecords = allBookings.filter(booking => 
      booking.rentalId === rentalId &&
      (
          (new Date(booking.arrive) < new Date(depart) && new Date(booking.depart) > new Date(depart)) ||
          (new Date(booking.arrive) < new Date(arrive) && new Date(booking.depart) > new Date(arrive)) ||
          (new Date(booking.arrive) >= new Date(arrive) && new Date(booking.depart) <= new Date(depart))
      )
  );
  
  return doubleBookingRecords;

};